import type { ComponentPropsWithoutRef, Ref } from 'react'
import { cn } from '#utils'

export interface CodeDisplayProps extends ComponentPropsWithoutRef<'div'> {
  /** The access code. `null` renders placeholder boxes. */
  code: string | null
  /** Whether the access window is open. `false` renders placeholders regardless of `code`. */
  available: boolean
  /** Placeholder box count when there is no code to show. */
  placeholderLength?: number
  /** What a screen reader calls the code — "Access code", or "Bay PIN" on the PIN step. */
  label?: string
  ref?: Ref<HTMLDivElement>
}

/**
 * A door PIN or a Bay PIN, one character per box — filled tiles sharing the row, as the access
 * frames draw them (simulator-companion D77's "Step 2 Bay PIN"; the door's code step alike).
 *
 * ## How this is announced, and why it is not a labelled group
 *
 * The boxes must not be read as loose characters — a screen reader walking four sibling
 * `div`s says "4, 8, 2, 7" with no indication they form one code or what it opens.
 *
 * The obvious fix, `role="group"` + `aria-label` with the boxes `aria-hidden`, does not
 * reliably work: group names are announced inconsistently, and VoiceOver typically only
 * announces them for focusable groups. A member could land on this panel and find the PIN
 * simply unreadable — the one piece of information the screen exists to convey.
 *
 * So the value lives in a visually-hidden text node, which every screen reader reads because
 * it is ordinary text, and the boxes stay `aria-hidden` as the purely visual presentation.
 * Digits are spaced apart so they are read individually rather than as one large number.
 *
 * @example
 * ```tsx
 * <CodeDisplay code="4827" available />
 * <CodeDisplay code={null} available={false} />
 * ```
 */
export function CodeDisplay({
  code,
  available,
  placeholderLength = 4,
  label = 'Access code',
  className,
  ref,
  ...props
}: CodeDisplayProps) {
  const showCode = available && !!code
  const characters = showCode ? code.split('') : Array<string>(placeholderLength).fill('-')

  return (
    // A definite width: the panels centre their children in a column that shrinks to fit, so a
    // percentage would collapse the tiles. The frames' 343px row, narrower on a narrow screen.
    <div ref={ref} className={cn('flex w-[343px] max-w-full items-center justify-center gap-4', className)} {...props}>
      <span className="sr-only">
        {showCode ? `${label}: ${code.split('').join(' ')}` : `${label} not available yet`}
      </span>

      {characters.map((character, index) => (
        <div
          key={index}
          aria-hidden="true"
          className={cn(
            'flex h-14 min-w-0 flex-1 items-center justify-center rounded-[18px] border bg-[#f9f9f9] text-xl font-bold',
            showCode
              ? 'border-[#cdcdcd] text-[#111]'
              : 'border-butter-gray-100 text-butter-gray-300',
          )}
        >
          {character}
        </div>
      ))}
    </div>
  )
}
